import { useMemo, type ReactNode } from "react";
import "@/pages/RoleDefaults.css";

export type RoleFlagKey =
  | "superAdmin"
  | "admin"
  | "teacher"
  | "student"
  | "curriculumDeveloper"
  | "counselor"
  | "parent";

export type RoleFlags = Record<RoleFlagKey, boolean>;

export type InstructorKitFlags = {
  proposeActivities: boolean;
  editActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
};

export type SupervisorKitFlags = {
  viewActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
  studentProgressChat: boolean;
  staffProgressChat: boolean;
};

export type MessageKinds = { office: boolean };

export type LinkTypeFlags = {
  guardians: boolean;
  counselors: boolean;
  siblings: boolean;
};

export type RelationshipTypeFlags = {
  guardian: boolean;
  sibling: boolean;
  teacher_of_record: boolean;
  counselor: boolean;
  supervisor: boolean;
};

export type AttendanceActionFlags = {
  insert: boolean;
  update: boolean;
};

export type EventActionFlags = {
  insert: boolean;
  edit: boolean;
  remove: boolean;
};

export type SourceKitFlags = {
  document: boolean;
  image: boolean;
  audio: boolean;
  video: boolean;
  url: boolean;
  replaceFile: boolean;
  editAnalysis: boolean;
  max12mb: boolean;
  max80mb: boolean;
  max200mb: boolean;
};

export interface RolePermissions {
  pages: string[];
  capabilities: string[];
  addUserRoles: RoleFlags;
  viewUserRoles: RoleFlags;
  inboxRoles: RoleFlags;
  instructorKit: InstructorKitFlags;
  supervisorKit: SupervisorKitFlags;
  messageKinds: MessageKinds;
  linkTypes: LinkTypeFlags;
  relationshipTypes: RelationshipTypeFlags;
  sourceKit: SourceKitFlags;
  eventActions: EventActionFlags;
  attendanceActions: AttendanceActionFlags;
}

export interface CatalogPage {
  id: string;
  label: string;
  description: string;
}

export interface CatalogCapability {
  id: string;
  label: string;
  description: string;
  group: string;
  groupLabel: string;
  groupHint?: string;
  addRoles?: boolean;
  viewRoles?: boolean;
  inboxRoles?: boolean;
  instructorKit?: boolean;
  supervisorKit?: boolean;
  messageKinds?: boolean;
  linkTypes?: boolean;
  relationshipTypes?: boolean;
  sourceKit?: boolean;
  eventActions?: boolean;
  attendanceActions?: boolean;
  kitOptions?: { id: string; slug: string; label: string; disabled?: boolean }[];
}

export interface RoleFlagOption {
  id: RoleFlagKey;
  label: string;
}

export function emptyRoleFlags(): RoleFlags {
  return {
    superAdmin: false,
    admin: false,
    teacher: false,
    student: false,
    curriculumDeveloper: false,
    counselor: false,
    parent: false,
  };
}

export function emptyInstructorKit(on = false): InstructorKitFlags {
  return {
    proposeActivities: on,
    editActivities: on,
    gradeActivities: on,
    overrideActivities: on,
  };
}

export function emptyAttendanceActions(on = false): AttendanceActionFlags {
  return { insert: on, update: on };
}

export function emptyEventActions(on = false): EventActionFlags {
  return { insert: on, edit: on, remove: on };
}

export function emptySupervisorKit(on = false): SupervisorKitFlags {
  return {
    viewActivities: on,
    gradeActivities: on,
    overrideActivities: on,
    studentProgressChat: on,
    staffProgressChat: on,
  };
}

function emptyLinkTypes(on?: Partial<LinkTypeFlags>): LinkTypeFlags {
  return {
    guardians: on?.guardians === true,
    counselors: on?.counselors === true,
    siblings: on?.siblings === true,
  };
}

function emptyRelationshipTypes(on?: Partial<RelationshipTypeFlags>): RelationshipTypeFlags {
  return {
    guardian: on?.guardian === true,
    sibling: on?.sibling === true,
    teacher_of_record: on?.teacher_of_record === true,
    counselor: on?.counselor === true,
    supervisor: on?.supervisor === true,
  };
}

function relationshipTypesUnset(flags: RelationshipTypeFlags | undefined): boolean {
  return !flags?.guardian && !flags?.sibling && !flags?.teacher_of_record && !flags?.counselor && !flags?.supervisor;
}

function emptySourceKit(on = false): SourceKitFlags {
  return {
    document: on,
    image: on,
    audio: on,
    video: on,
    url: on,
    replaceFile: on,
    editAnalysis: on,
    max12mb: false,
    max80mb: false,
    max200mb: false,
  };
}

function sourceKitUnset(flags: SourceKitFlags | undefined): boolean {
  if (!flags) return true;
  return !(
    flags.document ||
    flags.image ||
    flags.audio ||
    flags.video ||
    flags.url ||
    flags.replaceFile ||
    flags.editAnalysis
  );
}

function applyNestedDefaults(next: RolePermissions, cap: CatalogCapability | undefined) {
  if (!cap) return;
  if (cap.messageKinds && !next.messageKinds?.office) next.messageKinds = { office: true };
  if (
    cap.linkTypes &&
    !next.linkTypes?.guardians &&
    !next.linkTypes?.counselors &&
    !next.linkTypes?.siblings
  ) {
    next.linkTypes = emptyLinkTypes({ guardians: true, counselors: true });
  }
  if (cap.relationshipTypes && relationshipTypesUnset(next.relationshipTypes)) {
    next.relationshipTypes = emptyRelationshipTypes({ guardian: true, counselor: true });
  }
  if (cap.sourceKit && sourceKitUnset(next.sourceKit)) next.sourceKit = emptySourceKit(true);
  if (cap.eventActions && kitAllOff(next.eventActions)) next.eventActions = emptyEventActions(true);
  if (cap.attendanceActions && kitAllOff(next.attendanceActions)) {
    next.attendanceActions = emptyAttendanceActions(true);
  }
}

function messageKindHint(id: string): string {
  if (id === "messages.SendMessages") {
    return "Office is required. They may start an office request and view the threads they started. This does not open the office inbox. Support is not stored. This Office box is shared by Receive, Send, and Reply.";
  }
  if (id === "messages.ReplyMessages") {
    return "Office is required. Checked kinds are the inboxes they answer as staff. A parent continuing their own note uses Message Students Office. Support is not stored. This Office box is shared by Receive, Send, and Reply.";
  }
  return "Office is required. They can read the office inbox. This does not start a thread and does not reply. Support is not stored. This Office box is shared by Receive, Send, and Reply.";
}

export function kitAllOff(flags: Record<string, boolean> | undefined): boolean {
  return !flags || Object.values(flags).every((on) => !on);
}

export function clonePermissions(value: RolePermissions): RolePermissions {
  return {
    pages: [...value.pages],
    capabilities: [...value.capabilities],
    addUserRoles: { ...value.addUserRoles },
    viewUserRoles: { ...value.viewUserRoles },
    inboxRoles: { ...emptyRoleFlags(), ...value.inboxRoles },
    instructorKit: { ...emptyInstructorKit(), ...value.instructorKit },
    supervisorKit: { ...emptySupervisorKit(), ...value.supervisorKit },
    messageKinds: { office: value.messageKinds?.office === true },
    linkTypes: { ...emptyLinkTypes(), ...value.linkTypes },
    relationshipTypes: { ...emptyRelationshipTypes(), ...value.relationshipTypes },
    sourceKit: { ...emptySourceKit(), ...value.sourceKit },
    eventActions: { ...emptyEventActions(), ...value.eventActions },
    attendanceActions: { ...emptyAttendanceActions(), ...value.attendanceActions },
  };
}

export function fingerprint(value: RolePermissions): string {
  return JSON.stringify({
    pages: [...value.pages].sort(),
    capabilities: [...value.capabilities].sort(),
    addUserRoles: value.addUserRoles,
    viewUserRoles: value.viewUserRoles,
    inboxRoles: value.inboxRoles,
    instructorKit: value.instructorKit,
    supervisorKit: value.supervisorKit,
    messageKinds: value.messageKinds,
    linkTypes: value.linkTypes,
    relationshipTypes: value.relationshipTypes,
    sourceKit: value.sourceKit,
    eventActions: value.eventActions,
    attendanceActions: value.attendanceActions,
  });
}

function toggleId(list: string[], id: string, on: boolean): string[] {
  const next = new Set(list);
  if (on) next.add(id);
  else next.delete(id);
  return [...next];
}

function textMatches(needle: string, ...parts: Array<string | undefined>) {
  if (!needle) return false;
  return parts.some((part) => (part || "").toLowerCase().includes(needle));
}

function PermissionRow({
  id,
  label,
  description,
  checked,
  onToggle,
  expand,
  children,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onToggle: (on: boolean) => void;
  expand?: boolean;
  children?: ReactNode;
}) {
  return (
    <li data-permission-id={id}>
      <div className="al-role-defaults-row">
        <label className="al-role-defaults-check">
          <input type="checkbox" checked={checked} onChange={(event) => onToggle(event.target.checked)} />
          <span className="al-role-defaults-check-label">{label}</span>
        </label>
        <details className="al-role-defaults-more" key={expand ? "open" : "shut"} open={expand || undefined}>
          <summary>Details</summary>
          <span className="al-role-defaults-check-id">{id}</span>
          {description ? <span className="al-role-defaults-check-help">{description}</span> : null}
        </details>
      </div>
      {children}
    </li>
  );
}

export default function PermissionKitEditor({
  catalog,
  value,
  onChange,
  query,
  onQueryChange,
}: {
  catalog: {
    pages: CatalogPage[];
    capabilities: CatalogCapability[];
    roleFlags: RoleFlagOption[];
  };
  value: RolePermissions;
  onChange: (next: RolePermissions) => void;
  query: string;
  onQueryChange: (next: string) => void;
}) {
  const needle = query.trim().toLowerCase();
  const roleFlags = catalog.roleFlags || [];

  const visiblePages = useMemo(() => {
    const pages = catalog.pages || [];
    if (!needle) return pages;
    return pages.filter(
      (page) =>
        page.label.toLowerCase().includes(needle) ||
        page.id.toLowerCase().includes(needle) ||
        page.description.toLowerCase().includes(needle),
    );
  }, [catalog.pages, needle]);

  const capabilityGroups = useMemo(() => {
    const caps = catalog.capabilities || [];
    const filtered = needle
      ? caps.filter(
          (cap) =>
            cap.label.toLowerCase().includes(needle) ||
            cap.id.toLowerCase().includes(needle) ||
            cap.description.toLowerCase().includes(needle) ||
            (cap.kitOptions || []).some(
              (opt) =>
                opt.label.toLowerCase().includes(needle) ||
                opt.id.toLowerCase().includes(needle) ||
                opt.slug.toLowerCase().includes(needle),
            ),
        )
      : caps;
    const groups: { key: string; label: string; hint: string; items: CatalogCapability[] }[] = [];
    const index = new Map<string, number>();
    for (const cap of filtered) {
      let at = index.get(cap.group);
      if (at == null) {
        at = groups.length;
        index.set(cap.group, at);
        groups.push({ key: cap.group, label: cap.groupLabel, hint: cap.groupHint || "", items: [] });
      }
      groups[at].items.push(cap);
    }
    return groups;
  }, [catalog.capabilities, needle]);

  const patch = (updater: (current: RolePermissions) => RolePermissions) => {
    onChange(updater(value));
  };

  return (
    <div className="al-role-defaults-kit">
      <label className="al-role-defaults-filter">
        <span className="al-role-defaults-sr">Filter permissions</span>
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Filter pages and capabilities"
        />
      </label>
      <div className="al-role-defaults-grid al-role-defaults-grid--stack">
        <section className="al-role-defaults-panel">
          <h2>Pages</h2>
          <ul className="al-role-defaults-list">
            {visiblePages.map((page) => (
              <PermissionRow
                key={page.id}
                id={page.id}
                label={page.label}
                description={page.description}
                checked={value.pages.includes(page.id)}
                expand={textMatches(needle, page.id, page.label, page.description)}
                onToggle={(on) =>
                  patch((current) => ({
                    ...current,
                    pages: toggleId(current.pages, page.id, on),
                  }))
                }
              />
            ))}
          </ul>
        </section>
        <section className="al-role-defaults-panel">
          <h2>Capabilities</h2>
          {capabilityGroups.map((group) => {
            const onCount = group.items.filter((cap) => value.capabilities.includes(cap.id)).length;
            return (
              <details key={group.key} className="al-role-defaults-group" open={needle ? true : undefined}>
                <summary>
                  <span className="al-role-defaults-group-heading">
                    <span className="al-role-defaults-group-name">{group.label}</span>
                    {group.hint ? <span className="al-role-defaults-group-hint">{group.hint}</span> : null}
                  </span>
                  <span className="al-role-defaults-group-count">
                    {onCount}/{group.items.length}
                  </span>
                </summary>
                <ul className="al-role-defaults-list">
                  {group.items.map((cap) => {
                    const checked = value.capabilities.includes(cap.id);
                    const kitField = cap.instructorKit
                      ? "instructorKit"
                      : cap.supervisorKit
                        ? "supervisorKit"
                        : cap.eventActions
                          ? "eventActions"
                          : cap.attendanceActions
                            ? "attendanceActions"
                            : null;
                    return (
                      <PermissionRow
                        key={cap.id}
                        id={cap.id}
                        label={cap.label}
                        description={cap.description}
                        checked={checked}
                        expand={
                          textMatches(
                            needle,
                            cap.id,
                            cap.label,
                            cap.description,
                            ...(cap.kitOptions || []).flatMap((opt) => [opt.label, opt.id, opt.slug]),
                          )
                        }
                        onToggle={(on) =>
                          patch((current) => {
                            const next: RolePermissions = {
                              ...current,
                              capabilities: toggleId(current.capabilities, cap.id, on),
                            };
                            if (on && cap.instructorKit && kitAllOff(current.instructorKit)) {
                              next.instructorKit = emptyInstructorKit(true);
                            }
                            if (on && cap.supervisorKit && kitAllOff(current.supervisorKit)) {
                              next.supervisorKit = emptySupervisorKit(true);
                            }
                            if (on && cap.eventActions && kitAllOff(current.eventActions)) {
                              next.eventActions = emptyEventActions(true);
                            }
                            if (on && cap.attendanceActions && kitAllOff(current.attendanceActions)) {
                              next.attendanceActions = emptyAttendanceActions(true);
                            }
                            if (on) applyNestedDefaults(next, cap);
                            return next;
                          })
                        }
                      >
                        {checked && kitField && cap.kitOptions?.length ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              {cap.eventActions
                                ? "Create, edit, and delete. Leave all three checked to allow every verb. Uncheck all to allow none."
                                : cap.attendanceActions
                                  ? "The first mark, and changing a mark. Clearing is a change. Leave both checked to allow both."
                                  : cap.instructorKit
                                    ? "Ceiling for enrolled copies."
                                    : "Kit for copies they are not enrolled on."}
                            </p>
                            <div className="al-role-defaults-flags">
                              {cap.kitOptions.map((opt) => {
                                const kit = value[kitField] as Record<string, boolean>;
                                const on = kit[opt.id] === true;
                                return (
                                  <label key={`${cap.id}:${opt.id}`}>
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      onChange={(event) =>
                                        patch((current) => ({
                                          ...current,
                                          [kitField]: {
                                            ...current[kitField],
                                            [opt.id]: event.target.checked,
                                          },
                                        }))
                                      }
                                    />
                                    {opt.label}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : checked && (cap.addRoles || cap.viewRoles) ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              {cap.addRoles
                                ? "Which identities this person may add"
                                : "Which identities this person may view"}
                            </p>
                            <div className="al-role-defaults-flags">
                              {roleFlags
                                .filter(
                                  (flag) =>
                                    flag.id !== "parent" && !(cap.addRoles && flag.id === "superAdmin"),
                                )
                                .map((flag) => {
                                  const field = cap.addRoles ? "addUserRoles" : "viewUserRoles";
                                  const on = value[field][flag.id];
                                  return (
                                    <label key={`${cap.id}:${flag.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={(event) =>
                                          patch((current) => ({
                                            ...current,
                                            [field]: {
                                              ...current[field],
                                              [flag.id]: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                      {flag.label}
                                    </label>
                                  );
                                })}
                            </div>
                          </div>
                        ) : checked && cap.inboxRoles ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              Whose inboxes this person may read. At least one role. These are other people’s
                              direct messages, not this person’s own Messages.
                            </p>
                            <div className="al-role-defaults-flags">
                              {roleFlags.map((flag) => {
                                const on = value.inboxRoles?.[flag.id] === true;
                                return (
                                  <label key={`${cap.id}:${flag.id}`}>
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      onChange={(event) =>
                                        patch((current) => ({
                                          ...current,
                                          inboxRoles: {
                                            ...emptyRoleFlags(),
                                            ...current.inboxRoles,
                                            [flag.id]: event.target.checked,
                                          },
                                        }))
                                      }
                                    />
                                    {flag.label}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : checked && cap.messageKinds && cap.kitOptions?.length ? (
                          <div className="al-role-defaults-nested">
                            <p>{messageKindHint(cap.id)}</p>
                            <div className="al-role-defaults-flags">
                              {cap.kitOptions.map((opt) =>
                                opt.disabled ? (
                                  <label key={`${cap.id}:${opt.id}`} className="is-locked">
                                    <input type="checkbox" checked={false} disabled />
                                    {opt.label}
                                  </label>
                                ) : (
                                  <label key={`${cap.id}:${opt.id}`}>
                                    <input
                                      type="checkbox"
                                      checked={value.messageKinds?.office === true}
                                      onChange={(event) =>
                                        patch((current) => ({
                                          ...current,
                                          messageKinds: { office: event.target.checked },
                                        }))
                                      }
                                    />
                                    {opt.label}
                                  </label>
                                ),
                              )}
                            </div>
                          </div>
                        ) : checked && cap.linkTypes && cap.kitOptions?.length ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              Which relationships this person may link. Parents are guardians, not a separate type.
                              At least one is required.
                            </p>
                            <div className="al-role-defaults-flags">
                              {cap.kitOptions.map((opt) => (
                                <label key={`${cap.id}:${opt.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={value.linkTypes?.[opt.id as keyof LinkTypeFlags] === true}
                                    onChange={(event) =>
                                      patch((current) => ({
                                        ...current,
                                        linkTypes: {
                                          ...emptyLinkTypes(),
                                          ...current.linkTypes,
                                          [opt.id]: event.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                  {opt.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : checked && cap.relationshipTypes && cap.kitOptions?.length ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              Which person links this person may direct-message. The student is not a type. At least
                              one is required.
                            </p>
                            <div className="al-role-defaults-flags">
                              {cap.kitOptions.map((opt) => (
                                <label key={`${cap.id}:${opt.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={value.relationshipTypes?.[opt.id as keyof RelationshipTypeFlags] === true}
                                    onChange={(event) =>
                                      patch((current) => ({
                                        ...current,
                                        relationshipTypes: {
                                          ...emptyRelationshipTypes(),
                                          ...current.relationshipTypes,
                                          [opt.id]: event.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                  {opt.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : checked && cap.sourceKit && cap.kitOptions?.length ? (
                          <div className="al-role-defaults-nested">
                            <p>
                              Ceiling for Add, Edit, and Propose on this person. Leave the size boxes off to use the
                              Learn defaults.
                            </p>
                            <div className="al-role-defaults-flags">
                              {cap.kitOptions.map((opt) => (
                                <label key={`${cap.id}:${opt.id}`}>
                                  <input
                                    type="checkbox"
                                    checked={value.sourceKit?.[opt.id as keyof SourceKitFlags] === true}
                                    onChange={(event) =>
                                      patch((current) => ({
                                        ...current,
                                        sourceKit: {
                                          ...emptySourceKit(),
                                          ...current.sourceKit,
                                          [opt.id]: event.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                  {opt.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </PermissionRow>
                    );
                  })}
                </ul>
              </details>
            );
          })}
        </section>
      </div>
    </div>
  );
}
